// eslint-disable-next-line no-restricted-imports
import { Formatter, Match, Parser, Route } from 'fp-ts-routing';

import {
  array,
  flow,
  monoid,
  option,
  pipe,
  readonlyArray,
  record,
  string,
} from '@code-expert/prelude';
import type { RequireNonUnion } from '@code-expert/type-utils';

type RequireStringLiteral<A extends string> = string extends A ? never : A;

const singleton: <K extends string, A>(
  k: RequireNonUnion<RequireStringLiteral<K>>,
  a: A,
) => Record<K, A> = record.singleton;

/**
 * Match the remaining route as a path.
 * Empty matches result in an empty string.
 * Non-empty matches start with a slash.
 */
export const path = <K extends string>(
  key: RequireNonUnion<RequireStringLiteral<K>>,
): Match<Record<K, string>> =>
  new Match(
    new Parser(({ parts, query }) =>
      option.some([
        singleton(
          key,
          pipe(
            parts,
            array.match(() => '', flow(monoid.concatAll(string.join('/')), string.prepend('/'))),
          ),
        ),
        new Route([], query),
      ]),
    ),
    new Formatter(
      ({ parts, query }, { [key]: path }) =>
        new Route(
          pipe(
            parts,
            array.concat(
              pipe(path, string.split('/'), readonlyArray.toArray, array.filter(string.isNotBlank)),
            ),
          ),
          query,
        ),
    ),
  );
